import { Injectable, inject } from '@angular/core';
import { lastValueFrom, Observable } from 'rxjs';
import { KybApiService } from './kyb-api.service';
import { ConfigService } from '@app/core/config/config.service';
import {
  KybProcessMeta,
  KybProcessState,
  KybProcessStep,
  KybStateDocument,
  KybStateShareholder,
} from '../interfaces/kyb';

export type KybPersonType = 'PFAE' | 'PM';

/** Producto comercial seleccionado en /products. Define el conjunto de rutas
 *  y la forma del stepper (POS no incluye applicantForm UI-only). */
export type KybProduct = 'SUITE' | 'POS' | 'CARDS';

/** Mapeo producto → personType → step → ruta. El backend manda el array
 *  `steps` en /state; el componente sólo conoce el step y pregunta al
 *  servicio por la ruta concreta. */
const KYB_ROUTES: Record<KybProduct, Record<KybPersonType, Partial<Record<KybProcessStep, string>>>> = {
  SUITE: {
    PM: {
      CONTACT_INFO: '/kyb-pm/contact-info',
      COMPANY_INFO: '/kyb-pm/company-data',
      SAT_INFO: '/kyb-pm/visualization-sat',
      LEGAL_REP: '/kyb-pm/legal-representative',
      // 2026-05-20: backend mantiene SHAREHOLDERS como step independiente
      // entre LEGAL_REP y DOCUMENTS, pero la maqueta agrupa la captura de
      // accionistas dentro de Documentación (sub-sección 6.3). Mapeamos
      // SHAREHOLDERS a la misma ruta que DOCUMENTS y avanzamos backend
      // de SHAREHOLDERS→DOCUMENTS dentro del onContinue de Documentación.
      SHAREHOLDERS: '/kyb-pm/documentation',
      DOCUMENTS: '/kyb-pm/documentation',
      // NEW 2026-05-19 — FORM_INFO ahora es step backend para PM+SUITE
      // (paso 7 del sidebar). Pantalla applicant-form PM, mismo patrón que PFAE.
      FORM_INFO: '/kyb-pm/applicant-form',
      REVIEW: '/kyb-pm/final-registration',
    },
    PFAE: {
      CONTACT_INFO: '/kyb-fisic/contact-info',
      SAT_INFO: '/kyb-fisic/visualization-sat',
      LEGAL_REP: '/kyb-fisic/legal-representative',
      DOCUMENTS: '/kyb-fisic/documentation',
      // NEW 2026-05-19 — FORM_INFO ahora es step backend (antes UI-only).
      // Route: misma pantalla applicant-form pero protegida por kybStepGuard('FORM_INFO').
      FORM_INFO: '/kyb-fisic/applicant-form',
      REVIEW: '/kyb-fisic/final-registration',
    },
  },
  // POS reusa los mismos componentes KYB excepto Documentación (componente
  // dedicado en src/app/onboarding/pos-fisic/pages/documentation). No tiene
  // paso UI-only "applicantForm" — al terminar Documentación va directo a
  // Registro finalizado.
  POS: {
    PM: {
      CONTACT_INFO: '/pos-pm/contact-info',
      COMPANY_INFO: '/pos-pm/company-data',
      SAT_INFO: '/pos-pm/visualization-sat',
      LEGAL_REP: '/pos-pm/legal-representative',
      DOCUMENTS: '/pos-pm/documentation',
      REVIEW: '/pos-pm/final-registration',
    },
    PFAE: {
      CONTACT_INFO: '/pos-fisic/contact-info',
      SAT_INFO: '/pos-fisic/visualization-sat',
      LEGAL_REP: '/pos-fisic/legal-representative',
      DOCUMENTS: '/pos-fisic/documentation',
      REVIEW: '/pos-fisic/final-registration',
    },
  },
  // CARDS no entra al flujo KYB:
  //  - PFAE y PM ambos van a /cards/kyc/* (productsComponent rutea ambos casos).
  //  - Los componentes kyc/pages/* son product-agnostic y la navegación es
  //    interna (no depende de este KYB_ROUTES).
  //  - Incluido sólo para evitar índices vacíos al consultar el mapa.
  CARDS: {
    PM: {},
    PFAE: {},
  },
};

/** Guarda el estado de los formularios del flujo KYB + lógica de navegación backend-driven. */
@Injectable({
  providedIn: 'root',
})
export class KybFlowStateService {
  private readonly kybApi = inject(KybApiService);
  private readonly configService = inject(ConfigService);

  private personType: KybPersonType;
  /** Producto comercial activo en el flujo (POS, SUITE, CARDS). Determina
   *  rutas, copies y forma del stepper. Source of truth: `processMeta.product`
   *  cuando hay hidratación backend; fallback al valor local persistido en
   *  sessionStorage para sobrevivir F5 antes del primer GET /state. */
  private localProduct: KybProduct = 'SUITE';
  private contactInfo: Record<string, unknown> | null = null;
  private companyData: Record<string, unknown> | null = null;
  private visualizationSat: Record<string, unknown> | null = null;
  private legalRepresentative: Record<string, unknown> | null = null;
  private documentation: Record<string, unknown> | null = null;
  private shareholders: Record<string, unknown> | null = null;
  /** Formulario del solicitante (paso 6 PFAE). UI-only por ahora: no se envía a
   *  backend en este paso — se persiste localmente para sobrevivir back-nav y
   *  se incluirá en el submit final de Registro finalizado cuando exista el
   *  endpoint correspondiente. */
  private applicantForm: Record<string, unknown> | null = null;
  /** Secciones extras de Documentación PFAE (5.3 cónyuge, 5.4 propietario real,
   *  5.5 proveedores, 5.6 beneficiarios, 5.7 info operativa). UI-only por ahora:
   *  los `documentType` correspondientes todavía no existen en backend, así que
   *  estos datos se persisten localmente y se enviarán cuando el contrato esté
   *  definido. Ver PfaeExtraSectionsComponent. */
  private pfaeExtras: Record<string, unknown> | null = null;
  /** Secciones extras de Documentación PM (6.3 accionistas con suma ≤60%, 6.4
   *  propietario real, 6.5 proveedor de recursos condicional, 6.6 beneficiarios
   *  con suma 100%, 6.7 info operativa). Mismo motivo que pfaeExtras: UI-only
   *  hasta que el contrato backend incluya los documentTypes nuevos. Ver
   *  PmDocumentExtrasComponent. */
  private pmDocExtras: Record<string, unknown> | null = null;
  /** True cuando el flujo se reabrió desde el link del correo (ResumeComponent).
   *  Habilita affordances especiales como "Cambiar CIEC" en SAT, que solo
   *  aplican a usuarios que retomaron un proceso ya iniciado. Persistido en
   *  sessionStorage para que sobreviva F5 / back-nav dentro del flow. */
  private _enteredViaResume = false;
  /** Contacto del beneficiario real (PFAE/PM). Hidratado desde GET /state y consumido
   *  por documentation.component al iniciar para pre-llenar teléfono y email. */
  private beneficialOwnerContact: { phone?: string; email?: string } | null = null;

  // Estado de navegación backend-driven
  private _processMeta: KybProcessMeta | null = null;
  private _stepsArray: KybProcessStep[] = [];
  private _shownStep: KybProcessStep | null = null;
  private _hydrated = false;
  private _hydrating: Promise<void> | null = null;
  private _stateDocuments: KybStateDocument[] = [];
  private _stateShareholders: KybStateShareholder[] = [];

  constructor() {
    const saved = sessionStorage.getItem('kybPersonType') as KybPersonType | null;
    this.personType = saved === 'PM' ? 'PM' : 'PFAE';
    const savedProduct = sessionStorage.getItem('kybProduct') as KybProduct | null;
    this.localProduct = savedProduct === 'POS' || savedProduct === 'CARDS' ? savedProduct : 'SUITE';
    this._enteredViaResume = sessionStorage.getItem('kybEnteredViaResume') === '1';
  }

  // ─── Resume URL marker ──────────────────────────────────────────
  get enteredViaResume(): boolean {
    return this._enteredViaResume;
  }

  /** Lo invoca ResumeComponent al hidratar el flujo desde el link del correo. */
  markEnteredViaResume(): void {
    this._enteredViaResume = true;
    sessionStorage.setItem('kybEnteredViaResume', '1');
  }

  // ─── PersonType ─────────────────────────────────────────────────
  getPersonType(): KybPersonType {
    return this.personType;
  }

  setPersonType(value: KybPersonType): void {
    this.personType = value;
    sessionStorage.setItem('kybPersonType', value);
  }

  get isPFAE(): boolean {
    return this.personType === 'PFAE';
  }

  // ─── Producto ───────────────────────────────────────────────────
  /** Producto activo. Prioridad: processMeta del backend > fallback local. */
  get product(): KybProduct {
    const fromMeta = this._processMeta?.product as KybProduct | undefined;
    if (fromMeta === 'POS' || fromMeta === 'SUITE' || fromMeta === 'CARDS') return fromMeta;
    return this.localProduct;
  }

  get isPOS(): boolean {
    return this.product === 'POS';
  }

  /** Lo invoca ProductsComponent antes de POST /start para alinear la nav
   *  local con el producto que se va a registrar en backend. */
  setProduct(value: KybProduct): void {
    this.localProduct = value;
    sessionStorage.setItem('kybProduct', value);
  }

  // ─── Step labels (sidebar) ──────────────────────────────────────
  getStepLabels(t: (key: string) => string): { num: number; label: string; completed: boolean }[] {
    // POS no incluye el paso UI-only "Formulario del solicitante"; el flujo
    // va Documentación → Registro finalizado directo.
    const labels = this.buildLabels(t);
    // Marcar como completados todos los steps anteriores al currentStep del backend
    const currentIdx = this._processMeta ? this._stepsArray.indexOf(this._processMeta.currentStep) : -1;
    const shownNum = this._shownStep ? this.stepToSidebarNum(this._shownStep) : null;
    if (currentIdx >= 0 && shownNum) {
      labels.forEach(l => {
        if (l.num < shownNum) l.completed = true;
      });
    }
    return labels;
  }

  /** Construye los labels del stepper según producto y personType. */
  private buildLabels(t: (key: string) => string): { num: number; label: string; completed: boolean }[] {
    if (this.isPOS) {
      // POS PFAE (6 pasos): productSelection → contactInfo → SAT → legalRep
      //   → documentation → finalRegistration.
      // POS PM   (7 pasos): productSelection → contactInfo → companyData
      //   → SAT → legalRep → documentation → finalRegistration.
      return this.isPFAE
        ? [
            { num: 1, label: t('kyb.steps.productSelection'), completed: false },
            { num: 2, label: t('kyb.steps.contactInfo'), completed: false },
            { num: 3, label: t('kyb.steps.satLinking'), completed: false },
            { num: 4, label: t('kyb.steps.legalRep'), completed: false },
            { num: 5, label: t('kyb.steps.documentation'), completed: false },
            { num: 6, label: t('kyb.steps.finalRegistration'), completed: false },
          ]
        : [
            { num: 1, label: t('kyb.steps.productSelection'), completed: false },
            { num: 2, label: t('kyb.steps.contactInfo'), completed: false },
            { num: 3, label: t('kyb.steps.companyData'), completed: false },
            { num: 4, label: t('kyb.steps.satLinking'), completed: false },
            { num: 5, label: t('kyb.steps.legalRep'), completed: false },
            { num: 6, label: t('kyb.steps.documentation'), completed: false },
            { num: 7, label: t('kyb.steps.finalRegistration'), completed: false },
          ];
    }
    // SUITE: incluye paso UI-only "Formulario del solicitante" después de
    // Documentación (paso 6 PFAE / paso 7 PM).
    return this.isPFAE
      ? [
          { num: 1, label: t('kyb.steps.productSelection'), completed: false },
          { num: 2, label: t('kyb.steps.contactInfo'), completed: false },
          { num: 3, label: t('kyb.steps.satLinking'), completed: false },
          { num: 4, label: t('kyb.steps.legalRep'), completed: false },
          { num: 5, label: t('kyb.steps.documentation'), completed: false },
          { num: 6, label: t('kyb.steps.applicantForm'), completed: false },
          { num: 7, label: t('kyb.steps.finalRegistration'), completed: false },
        ]
      : [
          { num: 1, label: t('kyb.steps.productSelection'), completed: false },
          { num: 2, label: t('kyb.steps.contactInfo'), completed: false },
          { num: 3, label: t('kyb.steps.companyData'), completed: false },
          { num: 4, label: t('kyb.steps.satLinking'), completed: false },
          { num: 5, label: t('kyb.steps.legalRep'), completed: false },
          { num: 6, label: t('kyb.steps.documentation'), completed: false },
          // DEPRECATED 2026-04-28: paso SHAREHOLDERS eliminado del sidebar PM.
          // { num: 7, label: t('kyb.steps.shareholders'), completed: false },
          { num: 7, label: t('kyb.steps.applicantForm'), completed: false },
          { num: 8, label: t('kyb.steps.finalRegistration'), completed: false },
        ];
  }

  /** Convierte step del backend al numero del sidebar según producto y personType. */
  private stepToSidebarNum(step: KybProcessStep): number | null {
    if (this.isPOS) {
      // POS no inserta el paso UI-only applicantForm; REVIEW = paso 6 PFAE / 7 PM.
      const mapPM: Partial<Record<KybProcessStep, number>> = {
        CONTACT_INFO: 2, COMPANY_INFO: 3, SAT_INFO: 4, LEGAL_REP: 5, DOCUMENTS: 6, REVIEW: 7,
      };
      const mapPFAE: Partial<Record<KybProcessStep, number>> = {
        CONTACT_INFO: 2, SAT_INFO: 3, LEGAL_REP: 4, DOCUMENTS: 5, REVIEW: 6,
      };
      return (this.isPFAE ? mapPFAE : mapPM)[step] ?? null;
    }
    // SUITE (spec mayo 2026):
    //  - PFAE: 7 pasos visibles. FORM_INFO se inserta entre DOCUMENTS y REVIEW.
    //  - PM:   8 pasos visibles. FORM_INFO inserta entre DOCUMENTS y REVIEW.
    //          Confirmado contra maqueta Figma — NO hay paso SHAREHOLDERS
    //          separado; los accionistas viven dentro de DOCUMENTS (docs) y
    //          FORM_INFO (sub-sección "Formulario de datos de accionista").
    const mapPM: Partial<Record<KybProcessStep, number>> = {
      CONTACT_INFO: 2, COMPANY_INFO: 3, SAT_INFO: 4, LEGAL_REP: 5,
      // SHAREHOLDERS comparte slot de sidebar con DOCUMENTS (6) — la maqueta
      // agrupa la captura de accionistas dentro de Documentación.
      SHAREHOLDERS: 6, DOCUMENTS: 6, FORM_INFO: 7, REVIEW: 8,
    };
    const mapPFAE: Partial<Record<KybProcessStep, number>> = {
      CONTACT_INFO: 2, SAT_INFO: 3, LEGAL_REP: 4, DOCUMENTS: 5, FORM_INFO: 6, REVIEW: 7,
    };
    return (this.isPFAE ? mapPFAE : mapPM)[step] ?? null;
  }

  // ─── Form state (in-memory) ─────────────────────────────────────
  getContactInfo() { return this.contactInfo; }
  setContactInfo(value: Record<string, unknown> | null) { this.contactInfo = value; }

  getCompanyData() { return this.companyData; }
  setCompanyData(value: Record<string, unknown> | null) { this.companyData = value; }

  getVisualizationSat() { return this.visualizationSat; }
  setVisualizationSat(value: Record<string, unknown> | null) { this.visualizationSat = value; }

  getLegalRepresentative() { return this.legalRepresentative; }
  setLegalRepresentative(value: Record<string, unknown> | null) { this.legalRepresentative = value; }

  getDocumentation() { return this.documentation; }
  setDocumentation(value: Record<string, unknown> | null) { this.documentation = value; }

  getShareholders() { return this.shareholders; }
  setShareholders(value: Record<string, unknown> | null) { this.shareholders = value; }

  getApplicantForm() { return this.applicantForm; }
  setApplicantForm(value: Record<string, unknown> | null) { this.applicantForm = value; }

  getPfaeExtras() { return this.pfaeExtras; }
  setPfaeExtras(value: Record<string, unknown> | null) { this.pfaeExtras = value; }

  getPmDocExtras() { return this.pmDocExtras; }
  setPmDocExtras(value: Record<string, unknown> | null) { this.pmDocExtras = value; }

  /** Retorna el contacto del beneficiario real hidratado desde /state, o null si no
   *  fue capturado todavía. La pantalla de documentación lo usa para pre-llenar el form. */
  getBeneficialOwnerContact(): { phone?: string; email?: string } | null { return this.beneficialOwnerContact; }

  // ─── Backend-driven state + nav ─────────────────────────────────
  get isHydrated(): boolean { return this._hydrated; }
  get processMeta(): KybProcessMeta | null { return this._processMeta; }
  get stepsArray(): KybProcessStep[] { return this._stepsArray; }
  get currentStep(): KybProcessStep | null { return this._processMeta?.currentStep ?? null; }
  get shownStep(): KybProcessStep | null { return this._shownStep; }
  get stateDocuments(): KybStateDocument[] { return this._stateDocuments; }
  get stateShareholders(): KybStateShareholder[] { return this._stateShareholders; }
  get hasLegalRepresentative(): boolean { return this._processMeta?.hasLegalRepresentative === true; }
  get phoneVerified(): boolean { return this._processMeta?.phoneVerified === true; }
  get verifiedPhone(): string | null { return this._processMeta?.verifiedPhone ?? null; }

  /** Rehidrata el state desde GET /state. Idempotente; concurrent-safe. */
  async hydrateFromBackend(force = false): Promise<void> {
    if (this._hydrated && !force) return;
    // Modo mock (revisión de maquetas): no llamamos /state; sembramos processMeta
    // y stepsArray ficticios para que getNextStep/advanceCurrentStep funcionen.
    if (this.configService.isMockBackend) {
      this.seedMockState();
      return;
    }
    if (this._hydrating) return this._hydrating;
    this._hydrating = this.doHydrate();
    try { await this._hydrating; } finally { this._hydrating = null; }
  }

  private seedMockState(): void {
    // 2026-05-19 spec mayo 2026 — stepsArray es product-aware:
    //   PFAE+SUITE → CONTACT_INFO, SAT_INFO, LEGAL_REP, DOCUMENTS, FORM_INFO, REVIEW.
    //   PFAE+POS   → sin FORM_INFO (Documentación directo a Registro).
    //   PM+SUITE   → CONTACT_INFO, COMPANY_INFO, SAT_INFO, LEGAL_REP, DOCUMENTS, FORM_INFO, REVIEW.
    //                Maqueta confirma 8 pasos visibles (Fase 3 — sin pantalla
    //                Shareholders separada; accionistas viven dentro de DOCUMENTS
    //                y FORM_INFO).
    //   PM+POS     → flujo actual sin cambios (sin FORM_INFO).
    //   CARDS      → fases posteriores.
    const isSuite = this.localProduct === 'SUITE';
    const stepsPM: KybProcessStep[] = isSuite
      ? ['CONTACT_INFO', 'COMPANY_INFO', 'SAT_INFO', 'LEGAL_REP', 'SHAREHOLDERS', 'DOCUMENTS', 'FORM_INFO', 'REVIEW', 'FINISHED']
      : ['CONTACT_INFO', 'COMPANY_INFO', 'SAT_INFO', 'LEGAL_REP', 'DOCUMENTS', 'REVIEW', 'FINISHED'];
    const stepsPFAE: KybProcessStep[] = isSuite
      ? ['CONTACT_INFO', 'SAT_INFO', 'LEGAL_REP', 'DOCUMENTS', 'FORM_INFO', 'REVIEW', 'FINISHED']
      : ['CONTACT_INFO', 'SAT_INFO', 'LEGAL_REP', 'DOCUMENTS', 'REVIEW', 'FINISHED'];
    this._processMeta = {
      id: 'MOCK_ID',
      personType: this.personType,
      // En mock seedeamos con el producto local (set por ProductsComponent)
      // para que getRouteForStep y el stepper respeten POS/SUITE.
      product: this.localProduct,
      currentStep: 'CONTACT_INFO',
      status: 'IN_PROGRESS',
      hasLegalRepresentative: true,
      phoneVerified: true,
      verifiedPhone: null,
      privacyPolicyAccepted: true,
    };
    this._stepsArray = this.isPFAE ? stepsPFAE : stepsPM;
    this._stateDocuments = [];
    this._stateShareholders = [];
    if (!this._shownStep) this._shownStep = 'CONTACT_INFO';
    this._hydrated = true;
  }

  private async doHydrate(): Promise<void> {
    const res = await lastValueFrom(this.kybApi.getState());
    if (!res?.success || !res?.data) throw new Error('Hydrate failed: backend returned no data');
    const s: KybProcessState = res.data;
    this._processMeta = s.process;
    this._stepsArray = s.steps || [];
    this._stateDocuments = s.documents || [];
    this._stateShareholders = s.shareholders || [];
    // Si nunca se setio shownStep, arrancar en el currentStep backend
    if (!this._shownStep) this._shownStep = s.process.currentStep;

    // Sincronizar personType
    if (s.process.personType === 'PM' || s.process.personType === 'PFAE') {
      this.setPersonType(s.process.personType);
    }

    // Poblar form state con lo guardado
    if (s.contactInfo) this.contactInfo = s.contactInfo;
    if (s.companyInfo) this.companyData = s.companyInfo;
    if (s.satInfo) this.visualizationSat = { ...s.satInfo };
    if (s.legalRepresentative) this.legalRepresentative = s.legalRepresentative;
    if (s.shareholders?.length) {
      // Los componentes shareholders esperan { accionistas: [...] }
      this.shareholders = { accionistas: s.shareholders };
    }
    // beneficialOwnerContact (telefono + email del beneficiario, capturados en
    // el step de Documentación). Guardamos el raw (`this.beneficialOwnerContact`)
    // y además lo pre-mapeamos al shape del FormGroup en `this.documentation`,
    // para que el ngOnInit de documentation.component lo levante via
    // `getDocumentation()` sin lógica adicional. Sin esto, al rehidratar el
    // flujo desde el resume URL el form de "Datos de contacto" aparecía vacío
    // aunque el backend tuviera la info.
    if (s.beneficialOwnerContact) {
      this.beneficialOwnerContact = s.beneficialOwnerContact;
      const bo = s.beneficialOwnerContact;
      const phoneDigits = typeof bo.phone === 'string' ? bo.phone.replace(/\D/g, '') : '';
      let countryCodeBenef = '+52';
      let telefonoBeneficiario = '';
      if (phoneDigits.length >= 10) {
        telefonoBeneficiario = phoneDigits.slice(-10);
        const codeDigits = phoneDigits.slice(0, -10);
        countryCodeBenef = codeDigits ? `+${codeDigits}` : '+52';
      } else {
        telefonoBeneficiario = phoneDigits;
      }
      this.documentation = {
        ...(this.documentation ?? {}),
        countryCodeBenef,
        telefonoBeneficiario,
        correoBeneficiario: bo.email ?? '',
      };
    }

    // 2026-05-19 spec mayo 2026 — hydrate de los nuevos slots (PFAE+SUITE):
    //  - solicitantForm → applicantForm (re-uso del slot existente)
    //  - businessContact, spouseDependent, operationalInfo, resourceProviders,
    //    beneficiaries → guardar en pfaeExtras combinado para que la UI los
    //    levante con `getPfaeExtras()`. (Cuando exista contrato 1:1 entre UI y
    //    backend, conviene mover cada uno a su propio slot.)
    if (s.solicitantForm) this.applicantForm = s.solicitantForm;
    const extrasHydrated: Record<string, unknown> = {};
    if (s.businessContact) extrasHydrated['businessContact'] = s.businessContact;
    if (s.spouseDependent) {
      extrasHydrated['hasSpouse'] = s.spouseDependent.hasSpouseOrDependents ? 'SI' : 'NO';
      extrasHydrated['spousePhone'] = s.spouseDependent.phone ?? '';
      extrasHydrated['spouseEmail'] = s.spouseDependent.email ?? '';
    }
    if (s.operationalInfo) {
      extrasHydrated['operativeVolume'] = s.operationalInfo.monthlyTransactionVolume;
      extrasHydrated['economicActivity'] = s.operationalInfo.economicActivity;
      extrasHydrated['operationCurrency'] = s.operationalInfo.operationCurrency;
      extrasHydrated['operatingCountries'] = s.operationalInfo.countriesOperated;
      extrasHydrated['commercialName'] = s.operationalInfo.commercialName;
    }
    if (s.resourceProviders?.length) extrasHydrated['proveedores'] = s.resourceProviders;
    if (s.beneficiaries?.length) extrasHydrated['beneficiarios'] = s.beneficiaries;
    if (Object.keys(extrasHydrated).length) {
      this.pfaeExtras = { ...(this.pfaeExtras ?? {}), ...extrasHydrated };
    }

    this._hydrated = true;
  }

  /** Marca el step que el usuario ve visualmente. Se llama en ngOnInit de cada step. */
  setShownStep(step: KybProcessStep): void {
    this._shownStep = step;
  }

  /** Decide si "Siguiente" debe llamar /next-step.
   * Solo cuando shownStep === currentStep (avanzando a un step nuevo). */
  shouldCallNextStep(): boolean {
    if (!this._shownStep || !this._processMeta) return true; // fallback seguro si no hidratamos
    return this._shownStep === this._processMeta.currentStep;
  }

  /** Se llama después de un POST /next-step exitoso para actualizar el maximo alcanzado en backend.
   * NO toca shownStep: el usuario sigue "viendo" el step actual hasta que navegue, y la pagina
   * destino reseteara shownStep en su ngOnInit. Si aqui moviesemos shownStep, getNextStep()
   * saltaria una pantalla. */
  advanceCurrentStep(newStep: KybProcessStep): void {
    if (this._processMeta) this._processMeta.currentStep = newStep;
  }

  /** Retorna el siguiente step del stepsArray a partir del shownStep actual. */
  getNextStep(): KybProcessStep | null {
    if (!this._shownStep || !this._stepsArray.length) return null;
    const idx = this._stepsArray.indexOf(this._shownStep);
    if (idx === -1 || idx >= this._stepsArray.length - 1) return null;
    return this._stepsArray[idx + 1];
  }

  /** Retorna el step anterior del stepsArray a partir del shownStep actual. */
  getPreviousStep(): KybProcessStep | null {
    if (!this._shownStep || !this._stepsArray.length) return null;
    const idx = this._stepsArray.indexOf(this._shownStep);
    if (idx <= 0) return null;
    return this._stepsArray[idx - 1];
  }

  /** Mapea un step a su ruta según producto y personType. */
  getRouteForStep(step: KybProcessStep | null): string | null {
    if (!step) return null;
    return KYB_ROUTES[this.product]?.[this.personType]?.[step] ?? null;
  }

  /** Ruta del paso UI-only "Formulario del solicitante" (Suite-only). Retorna
   *  null en POS porque el flujo va de Documentación → Registro finalizado sin
   *  pasar por esta pantalla. Los componentes que decidían avanzar al
   *  applicantForm deben hacer fallback a `getRouteForStep('REVIEW')` cuando
   *  esta función retorne null. */
  getApplicantFormRoute(): string | null {
    if (this.isPOS) return null;
    return this.isPFAE ? '/kyb-fisic/applicant-form' : '/kyb-pm/applicant-form';
  }

  /** Helper para "Siguiente": ejecuta save$, decide si llama /next-step, y retorna la ruta a navegar. */
  async processNextTransition(save$: Observable<any>): Promise<string | null> {
    // Modo mock (revisión de maquetas): avanza localmente sin tocar backend.
    if (this.configService.isMockBackend) {
      const next = this.getNextStep();
      if (next) this.advanceCurrentStep(next);
      return this.getRouteForStep(next);
    }
    await lastValueFrom(save$);
    if (this.shouldCallNextStep()) {
      const res: any = await lastValueFrom(this.kybApi.nextStep());
      const newStep = res?.data as KybProcessStep | undefined;
      if (newStep) this.advanceCurrentStep(newStep);
    }
    return this.getRouteForStep(this.getNextStep());
  }

  /** Helper para "Regresar": decide el step anterior y retorna la ruta a navegar. */
  processBackTransition(): string | null {
    const prev = this.getPreviousStep();
    if (!prev) return null;
    this.setShownStep(prev);
    return this.getRouteForStep(prev);
  }

  /** Refleja un cambio en hasLegalRepresentative tras guardar el step LEGAL_REP.
   * Si pasa de true→false, el backend borra rep legal y reseteamos el form local. */
  setHasLegalRepresentative(value: boolean): void {
    if (!this._processMeta) return;
    const prev = this._processMeta.hasLegalRepresentative;
    this._processMeta.hasLegalRepresentative = value;
    if (prev === true && value === false) {
      // Backend ya borro los datos. Limpiar local para no rehidratar zombies.
      this.legalRepresentative = null;
    }
  }

  /** Se llama despues de cambiar el telefono del rep legal; backend resetea phoneVerified. */
  markPhoneUnverified(): void {
    if (this._processMeta) {
      this._processMeta.phoneVerified = false;
      this._processMeta.verifiedPhone = null;
    }
  }

  /** Se llama tras un `confirmDocumentUpload` exitoso para que el doc recién
   *  subido en esta sesión sobreviva la navegación entre steps (sin esto, sólo
   *  los docs del hydrate inicial vuelven a renderizar al revisitar Documentos).
   *  Si ya existía un doc con el mismo `documentType`, lo reemplaza (un slot
   *  acepta un único doc). */
  addStateDocument(doc: KybStateDocument): void {
    const idx = this._stateDocuments.findIndex(d => d.documentType === doc.documentType);
    if (idx >= 0) {
      this._stateDocuments[idx] = doc;
    } else {
      this._stateDocuments.push(doc);
    }
  }

  /** Quita un doc del state local (no toca backend). Lo invocan los handlers
   *  de "Eliminar" en Documentación: como no hay endpoint DELETE, "borrar" es
   *  puramente UI — limpiamos también stateDocuments para que `applyStateDocuments`
   *  no rehidrate el slot al revisitar la pantalla. Si el usuario re-sube el
   *  mismo `documentType`, `addStateDocument` lo reemplaza con el nuevo
   *  `s3Key`/`documentId`. */
  removeStateDocumentByType(documentType: string): void {
    this._stateDocuments = this._stateDocuments.filter(d => d.documentType !== documentType);
  }

  /** Limpia todo el estado al finalizar el flujo. */
  clearAll(): void {
    this.personType = 'PFAE';
    sessionStorage.removeItem('kybPersonType');
    this.localProduct = 'SUITE';
    sessionStorage.removeItem('kybProduct');
    this.contactInfo = null;
    this.companyData = null;
    this.visualizationSat = null;
    this.legalRepresentative = null;
    this.documentation = null;
    this.shareholders = null;
    this.applicantForm = null;
    this.pfaeExtras = null;
    this.pmDocExtras = null;
    this.beneficialOwnerContact = null;
    this._processMeta = null;
    this._stepsArray = [];
    this._shownStep = null;
    this._hydrated = false;
    this._stateDocuments = [];
    this._stateShareholders = [];
    this._enteredViaResume = false;
    sessionStorage.removeItem('kybEnteredViaResume');
  }
}
